import { useState, useMemo } from "react";

// ─── PeriodicTableMCQuiz ────────────────────────────────────────────
//
// Multi-choice quiz over the element data already in the Periodic Table
// tool. Companion to QuizMode (which is flashcard-style). Distractors
// use getPeriodicNeighbors from the V62.5 periodicTable module so
// wrong answers cluster around the right one (Cs's neighbors are
// Rb/K/Na/Fr, not F/Be).
//
// Question types (chosen for varied difficulty + good distractor
// availability):
//   1. Symbol → Name           ("Fe" → Iron, Cu/Co/Cr distractors)
//   2. Name → Symbol           ("Iron" → Fe, Cu/Co/Cr distractors)
//   3. Element → Category      ("Silicon" → Metalloid)
//   4. Element → Block         ("Iron" → d-block)
//   5. Element → Atomic number ("Iron" → 26, periodic-neighbor numbers)
//   6. Element → Group         ("Iron" → 8)
//   7. Element → State at STP  ("Bromine" → liquid)
//
// Cards are generated per session — sampling rather than enumeration
// keeps the quiz short and replayable.

import { getPeriodicNeighbors, getElement } from "../data/periodicTable.js";

const CATEGORY_OPTIONS = [
  { value: "alkali",          label: "Alkali metal" },
  { value: "alkaline",        label: "Alkaline earth metal" },
  { value: "transition",      label: "Transition metal" },
  { value: "post-transition", label: "Post-transition metal" },
  { value: "metalloid",       label: "Metalloid" },
  { value: "nonmetal",        label: "Nonmetal" },
  { value: "halogen",         label: "Halogen" },
  { value: "noble",           label: "Noble gas" },
  { value: "lanthanide",      label: "Lanthanide" },
  { value: "actinide",        label: "Actinide" },
];

const BLOCK_OPTIONS = ["s", "p", "d", "f"];
const STATE_OPTIONS = ["solid", "liquid", "gas"];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function pickOne(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

// Build a single MC card from a random element. Picks a question type
// at random, returns { q, ctx, correct, options } or null if the
// chosen element doesn't have enough data to support that question.
//
// NOTE: getPeriodicNeighbors returns an array of SYMBOL STRINGS, not
// element objects. We hydrate to objects via getElement when we need
// to read other fields (name, number) for distractors.
function buildCard(elements, kindHint) {
  const el = pickOne(elements);
  const kind = kindHint || pickOne(["symbol-to-name", "name-to-symbol", "element-to-category", "element-to-block", "element-to-number", "element-to-group", "element-to-state"]);

  if (kind === "symbol-to-name") {
    const neighbors = getPeriodicNeighbors(el.symbol, 8).map(getElement).filter(Boolean).slice(0, 3);
    if (neighbors.length < 3) return null;
    return {
      q: `Which element has the symbol ${el.symbol}?`,
      ctx: `Period ${el.row}`,
      correct: el.name,
      options: shuffle([el.name, ...neighbors.map((n) => n.name)]),
    };
  }

  if (kind === "name-to-symbol") {
    const neighbors = getPeriodicNeighbors(el.symbol, 8).map(getElement).filter(Boolean).slice(0, 3);
    if (neighbors.length < 3) return null;
    return {
      q: `What is the chemical symbol for ${el.name}?`,
      ctx: `Period ${el.row}`,
      correct: el.symbol,
      options: shuffle([el.symbol, ...neighbors.map((n) => n.symbol)]),
    };
  }

  if (kind === "element-to-category") {
    if (!el.categoryLabel) return null;
    const wrong = CATEGORY_OPTIONS
      .filter((c) => c.value !== el.category)
      .map((c) => c.label);
    return {
      q: `Which category does ${el.name} (${el.symbol}) belong to?`,
      ctx: `Period ${el.row}, Group ${el.group ?? "—"}`,
      correct: el.categoryLabel,
      options: shuffle([el.categoryLabel, ...shuffle(wrong).slice(0, 3)]),
    };
  }

  if (kind === "element-to-block") {
    if (!el.block) return null;
    const wrong = BLOCK_OPTIONS.filter((b) => b !== el.block);
    return {
      q: `Which block of the periodic table does ${el.name} (${el.symbol}) belong to?`,
      ctx: `Period ${el.row}`,
      correct: `${el.block}-block`,
      options: shuffle([`${el.block}-block`, ...wrong.map((b) => `${b}-block`)]),
    };
  }

  if (kind === "element-to-number") {
    const neighbors = getPeriodicNeighbors(el.symbol, 8).map(getElement).filter(Boolean).slice(0, 3);
    if (neighbors.length < 3) return null;
    return {
      q: `What is the atomic number of ${el.name} (${el.symbol})?`,
      ctx: `${el.categoryLabel ?? "Element"}`,
      correct: String(el.number),
      options: shuffle([String(el.number), ...neighbors.map((n) => String(n.number))]),
    };
  }

  if (kind === "element-to-group") {
    if (el.group == null) return null; // f-block elements have null group
    // Distractors: nearby group numbers
    const groupsInRange = [];
    for (let g = Math.max(1, el.group - 4); g <= Math.min(18, el.group + 4); g++) {
      if (g !== el.group) groupsInRange.push(g);
    }
    const wrong = shuffle(groupsInRange).slice(0, 3);
    if (wrong.length < 3) return null;
    return {
      q: `Which group does ${el.name} (${el.symbol}) belong to?`,
      ctx: `Period ${el.row}`,
      correct: String(el.group),
      options: shuffle([String(el.group), ...wrong.map(String)]),
    };
  }

  if (kind === "element-to-state") {
    if (!el.state) return null;
    const wrong = STATE_OPTIONS.filter((s) => s !== el.state);
    return {
      q: `What is the state of ${el.name} (${el.symbol}) at standard temperature and pressure?`,
      ctx: `Period ${el.row}, ${el.categoryLabel ?? "Element"}`,
      correct: el.state.charAt(0).toUpperCase() + el.state.slice(1),
      options: shuffle([
        el.state.charAt(0).toUpperCase() + el.state.slice(1),
        ...wrong.map((s) => s.charAt(0).toUpperCase() + s.slice(1)),
      ]),
    };
  }

  return null;
}

// Generate `count` valid cards by sampling. Skips elements that can't
// produce a clean card for the chosen question type.
function buildDeck(elements, count = 15) {
  const cards = [];
  let attempts = 0;
  while (cards.length < count && attempts < count * 5) {
    attempts++;
    const card = buildCard(elements);
    if (card) cards.push(card);
  }
  return cards;
}

export default function PeriodicTableMCQuiz({ elements, accent, onExit }) {
  const deck = useMemo(() => buildDeck(elements, 15), [elements]);
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState({ right: 0, wrong: 0 });

  if (!deck.length) {
    return (
      <div style={{ textAlign: "center", padding: "28px 12px", color: "#9A9DBE", fontSize: 14 }}>
        Couldn't build a quiz from this element data.
        <div style={{ marginTop: 16 }}>
          <button onClick={onExit} style={{ padding: "10px 18px", borderRadius: 10, border: "none", background: accent, color: "#06121C", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>Back</button>
        </div>
      </div>
    );
  }

  if (i >= deck.length) {
    const total = score.right + score.wrong;
    const pct = total ? Math.round((100 * score.right) / total) : 0;
    return (
      <div style={{ textAlign: "center", padding: "32px 12px" }}>
        <div style={{ fontSize: 30, fontWeight: 800, color: accent }}>{pct}%</div>
        <p style={{ fontSize: 14, color: "#C8CADF", margin: "8px 0 20px" }}>
          {score.right} right · {score.wrong} wrong · {deck.length} cards
        </p>
        <button onClick={onExit} style={{ padding: "11px 20px", borderRadius: 10, border: "none", background: accent, color: "#06121C", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
          Back
        </button>
      </div>
    );
  }

  const card = deck[i];
  const resolved = picked !== null;

  const onPick = (option) => {
    if (resolved) return;
    setPicked(option);
    const correct = option === card.correct;
    setScore((s) => ({ right: s.right + (correct ? 1 : 0), wrong: s.wrong + (correct ? 0 : 1) }));
  };

  const onNext = () => {
    setPicked(null);
    setI(i + 1);
  };

  const optionStyle = (option) => {
    const base = {
      width: "100%", textAlign: "left", padding: "12px 14px", borderRadius: 10,
      fontSize: 14, fontWeight: 500, cursor: resolved ? "default" : "pointer",
      transition: "background 0.15s, border-color 0.15s",
      fontFamily: "inherit", marginBottom: 8,
    };
    if (!resolved) {
      return { ...base, border: `1px solid ${accent}55`, background: "transparent", color: "#E8E9F3" };
    }
    if (option === card.correct) {
      return { ...base, border: `1px solid #34C759`, background: "rgba(52,199,89,0.15)", color: "#7CEA9A" };
    }
    if (option === picked) {
      return { ...base, border: `1px solid #E24B4A`, background: "rgba(226,75,74,0.15)", color: "#FCA5A4" };
    }
    return { ...base, border: `1px solid ${accent}22`, background: "transparent", color: "#7C8099" };
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#7C8099", marginBottom: 12 }}>
        <span>Card {i + 1} / {deck.length}</span>
        <button onClick={onExit} style={{ background: "none", border: "none", color: "#7C8099", fontSize: 12, cursor: "pointer", textDecoration: "underline" }}>exit</button>
      </div>
      <div style={{ background: "#13141F", border: `1px solid ${accent}55`, borderRadius: 14, padding: "22px 18px", marginBottom: 14 }}>
        {card.ctx && <div style={{ fontSize: 11.5, color: "#7C8099", marginBottom: 6 }}>{card.ctx}</div>}
        <div style={{ fontSize: 17, fontWeight: 700, color: "#E8E9F3", lineHeight: 1.4 }}>{card.q}</div>
      </div>
      <div>
        {card.options.map((option) => (
          <button key={option} onClick={() => onPick(option)} style={optionStyle(option)} disabled={resolved}>
            {option}
          </button>
        ))}
      </div>
      {resolved && (
        <button onClick={onNext} style={{ width: "100%", marginTop: 6, padding: 13, borderRadius: 10, border: "none", background: accent, color: "#06121C", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
          {i + 1 < deck.length ? "Next →" : "See results"}
        </button>
      )}
    </div>
  );
}
